import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import * as auth from './auth';

/**
 * Ensure we have a valid token for API calls
 * @returns {Promise<string|null>} Valid token or null if authentication failed
 */
async function ensureValidToken() {
  try {
    console.log('[WorkflowUtils] Ensuring valid token for API calls');
    
    // Use the new validateToken method that handles validation and refresh
    const validToken = await auth.validateToken();
    
    if (validToken) {
      console.log('[WorkflowUtils] Valid token obtained');
      return validToken;
    }
    
    console.warn('[WorkflowUtils] Failed to obtain valid token');
    return null;
  } catch (error) {
    console.error('[WorkflowUtils] Error ensuring valid token:', error);
    return null;
  }
}

/**
 * Normalize workflow data from different API response formats
 * @param {Object} workflowData - The workflow data from API
 * @returns {Object} Normalized workflow data
 */
export const normalizeWorkflowData = (workflowData) => {
  if (!workflowData) return null;
  
  try {
    // Create a base normalized workflow object
    const normalizedWorkflow = {
      id: workflowData.id,
      name: workflowData.name,
      description: workflowData.description,
      workflowType: workflowData.workflowType || workflowData.type || 'unknown',
      variant: workflowData.variant || '',
      version: workflowData.version || 1,
      isActive: workflowData.isActive !== undefined ? workflowData.isActive : true,
      createdAt: workflowData.createdAt || new Date().toISOString(),
      steps: []
    };
    
    // Process steps from definition if available
    if (workflowData.definition) {
      let definitionObj;
      
      // Parse definition if it's a string
      if (typeof workflowData.definition === 'string') {
        try {
          definitionObj = JSON.parse(workflowData.definition);
        } catch (parseError) {
          console.error('Error parsing workflow definition:', parseError);
          definitionObj = { steps: [] };
        }
      } else {
        definitionObj = workflowData.definition;
      }
      
      // Extract steps from definition
      if (definitionObj && definitionObj.steps && Array.isArray(definitionObj.steps)) {
        normalizedWorkflow.steps = definitionObj.steps.map(step => ({
          id: step.id,
          name: step.name,
          serviceId: step.serviceId,
          serviceName: step.serviceName || step.name,
          required: step.required !== undefined ? step.required : true,
          governmentCharge: step.governmentCharge || '',
          serviceCharge: step.serviceCharge || '',
          taxRate: step.taxRate || ''
        }));
      }
    } else if (workflowData.steps && Array.isArray(workflowData.steps)) {
      // If steps are directly on the workflow object
      normalizedWorkflow.steps = workflowData.steps;
    }
    
    return normalizedWorkflow;
  } catch (error) {
    console.error('Error normalizing workflow data:', error);
    return workflowData; // Return original data if normalization fails
  }
};

/**
 * Extract steps from a workflow definition
 * @param {Object} workflowData - The workflow data
 * @returns {Array} The workflow steps
 */
export const extractWorkflowSteps = (workflowData) => {
  if (!workflowData) return [];
  
  try {
    // If steps are directly on the workflow object
    if (workflowData.steps && Array.isArray(workflowData.steps)) {
      return workflowData.steps;
    }
    
    // If steps are in the definition
    if (workflowData.definition) {
      let definitionObj;
      
      // Parse definition if it's a string
      if (typeof workflowData.definition === 'string') {
        try {
          definitionObj = JSON.parse(workflowData.definition);
        } catch (parseError) {
          console.error('Error parsing workflow definition:', parseError);
          return [];
        }
      } else {
        definitionObj = workflowData.definition;
      }
      
      // Extract steps from definition
      if (definitionObj && definitionObj.steps && Array.isArray(definitionObj.steps)) {
        return definitionObj.steps;
      }
    }
    
    return [];
  } catch (error) {
    console.error('Error extracting workflow steps:', error);
    return [];
  }
};

/**
 * Fetch a workflow by ID using token-based authentication
 * @param {string} workflowId - The ID of the workflow to fetch
 * @returns {Promise<Object>} The workflow data
 */
export const fetchWorkflowById = async (workflowId) => {
  try {
    console.log(`[WorkflowUtils] Fetching workflow with ID: ${workflowId}`);
    
    if (!workflowId) {
      console.error('[WorkflowUtils] No workflow ID provided');
      throw new Error('Workflow ID is required');
    }
    
    // Ensure we have a valid token
    const token = await ensureValidToken();
    
    if (!token) {
      console.warn('[WorkflowUtils] No valid token available for workflow fetch, attempting to login again');
      // Try to refresh the token by importing auth and calling login
      try {
        const authModule = await import('./auth');
        // This is a temporary login with default credentials - in production, this should be handled by the login UI
        const loginSuccess = await authModule.login('admin', 'admin');
        if (loginSuccess) {
          console.log('[WorkflowUtils] Successfully logged in again');
          // Get the new token
          const newToken = authModule.getToken();
          if (newToken) {
            console.log('[WorkflowUtils] New token obtained');
            // Continue with the new token
            return await fetchWorkflowById(workflowId); // Recursive call with new token
          }
        } else {
          console.error('[WorkflowUtils] Failed to login again');
        }
      } catch (loginError) {
        console.error('[WorkflowUtils] Error during login attempt:', loginError);
      }
    }
    
    console.log('[WorkflowUtils] Proceeding with workflow fetch');
    
    // Try different API endpoint patterns, prioritizing public endpoints
    const possibleEndpoints = [
      // Public API endpoints (no authentication required)
      `${API_BASE_URL}/v1/api/public/workflows/${workflowId}`,
      // Fallback to standard endpoints if public ones don't work
      `${API_BASE_URL}/api/workflows/${workflowId}`,
      `${API_BASE_URL}/v1/api/workflows/${workflowId}`,
      `${API_BASE_URL}/api/workflow/${workflowId}`,
      `${API_BASE_URL}/api/v1/workflows/${workflowId}`,
      `${API_BASE_URL}/workflows/${workflowId}`,
      `${API_BASE_URL}/workflow/${workflowId}`,
      `${API_BASE_URL}/api/workflow/get/${workflowId}`,
      `${API_BASE_URL}/api/workflows/get/${workflowId}`
    ];
    
    let response = null;
    let successEndpoint = '';
    
    // Try each endpoint until one works
    for (const endpoint of possibleEndpoints) {
      try {
        console.log(`[WorkflowUtils] Trying endpoint for workflow: ${endpoint}`);
        
        const result = await axios.get(endpoint, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          }
        });
        
        if (result && result.data) {
          response = result;
          successEndpoint = endpoint;
          console.log(`[WorkflowUtils] Successfully fetched workflow from ${successEndpoint}`);
          break;
        }
      } catch (endpointError) {
        console.warn(`[WorkflowUtils] Endpoint ${endpoint} failed:`, endpointError.message);
        // Continue to the next endpoint
      }
    }
    
    if (!response) {
      console.warn('[WorkflowUtils] All API endpoints failed for fetching workflow, returning null');
      return null; // Return null instead of throwing
    }
    
    return normalizeWorkflowData(response.data);
  } catch (error) {
    console.error('[WorkflowUtils] Error fetching workflow:', error);
    throw error;
  }
};

/**
 * Fetch all workflows using token-based authentication
 * @returns {Promise<Array>} The list of workflows
 */
export const fetchAllWorkflows = async () => {
  try {
    // Ensure we have a valid token before making API calls
    console.log('[WorkflowUtils] Ensuring valid authentication token for workflows fetch...');
    const validToken = await ensureValidToken();
    
    if (!validToken) {
      console.error('[WorkflowUtils] Failed to obtain a valid authentication token');
      throw new Error('Authentication failed. Please log in again.');
    }
    
    console.log('[WorkflowUtils] Valid token obtained for workflows fetch');
    
    // Try different API endpoint patterns, prioritizing public endpoints
    const possibleEndpoints = [
      // Public API endpoints (no authentication required)
      `${API_BASE_URL}/v1/api/public/workflows`,
      // Fallback to authenticated endpoints
      `${API_BASE_URL}/api/workflows`,
      `${API_BASE_URL}/v1/api/workflows`
    ];
    
    let response = null;
    let successEndpoint = '';
    
    // Try each endpoint until one works
    for (const endpoint of possibleEndpoints) {
      try {
        console.log(`[WorkflowUtils] Trying endpoint for all workflows: ${endpoint}`);
        
        const headers = {
          'Authorization': `Bearer ${validToken}`,
          'Content-Type': 'application/json'
        };
        
        const result = await axios.get(endpoint, { headers });
        
        if (result && result.data) {
          response = result;
          successEndpoint = endpoint;
          console.log(`[WorkflowUtils] Successfully fetched all workflows from ${successEndpoint}`);
          break;
        }
      } catch (endpointError) {
        console.warn(`[WorkflowUtils] Endpoint ${endpoint} failed:`, endpointError.message);
        // Continue to the next endpoint
      }
    }
    
    if (!response) {
      throw new Error('All API endpoints failed for fetching workflows');
    }
    
    if (response && response.data && Array.isArray(response.data)) {
      console.log('[WorkflowUtils] Successfully fetched workflows:', response.data.length);
      return response.data;
    }
    
    throw new Error('No data returned from API or data is not an array');
  } catch (error) {
    console.error('[WorkflowUtils] Error fetching workflows:', error);
    throw error;
  }
};

/**
 * Fetch service mappings for a customer organization using token-based authentication
 * @param {string} customerId - The ID of the customer organization
 * @returns {Promise<Array>} The list of service mappings
 */
/**
 * Fetch services associated with a workflow
 * @param {string} workflowId - The ID of the workflow
 * @returns {Promise<Array>} The list of services associated with the workflow
 */
export const fetchWorkflowServices = async (workflowId) => {
  try {
    if (!workflowId) {
      console.error('[WorkflowUtils] No workflow ID provided');
      return []; // Return empty array instead of throwing
    }
    
    // Try to get token from localStorage first
    console.log('[WorkflowUtils] Getting token for workflow services...');
    let token = localStorage.getItem('token');
    console.log('[WorkflowUtils] Token exists:', !!token);
    
    // If no token in localStorage, try to get it from auth service
    if (!token) {
      try {
        token = await ensureValidToken();
        console.log('[WorkflowUtils] Token from auth service exists:', !!token);
      } catch (tokenError) {
        console.warn('[WorkflowUtils] Error getting token from auth service:', tokenError);
      }
    }
    
    // Try different API endpoint patterns, prioritizing public endpoints
    const possibleEndpoints = [
      // Public API endpoints (no authentication required)
      `${API_BASE_URL}/v1/api/public/workflows/${workflowId}/services`,
      `${API_BASE_URL}/v1/api/public/workflows/${workflowId}/steps`,
      // Fallback to authenticated endpoints
      `${API_BASE_URL}/api/workflows/${workflowId}/services`,
      `${API_BASE_URL}/v1/workflows/${workflowId}/services`,
      `${API_BASE_URL}/api/workflows/${workflowId}/steps`
    ];
    
    let response = null;
    let successEndpoint = '';
    
    // Try each endpoint with token authentication first
    if (token) {
      console.log('[WorkflowUtils] Trying endpoints with JWT token');
      
      for (const endpoint of possibleEndpoints) {
        try {
          console.log(`[WorkflowUtils] Trying endpoint with JWT token: ${endpoint}`);
          
          const result = await axios.get(endpoint, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (result && result.data) {
            response = result;
            successEndpoint = endpoint;
            console.log(`[WorkflowUtils] Successfully fetched workflow services with JWT token from ${successEndpoint}`);
            break;
          }
        } catch (endpointError) {
          console.warn(`[WorkflowUtils] JWT token endpoint ${endpoint} failed:`, endpointError.message);
          // Continue to the next endpoint
        }
      }
    }
    
    // If JWT token authentication failed, try with basic auth
    if (!response) {
      console.log('[WorkflowUtils] JWT token authentication failed, trying with basic auth');
      
      for (const endpoint of possibleEndpoints) {
        try {
          console.log(`[WorkflowUtils] Trying endpoint with basic auth: ${endpoint}`);
          
          const result = await axios.get(endpoint, {
            headers: {
              'Authorization': `Basic ${btoa('admin:admin')}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (result && result.data) {
            response = result;
            successEndpoint = endpoint;
            console.log(`[WorkflowUtils] Successfully fetched workflow services with basic auth from ${successEndpoint}`);
            break;
          }
        } catch (endpointError) {
          console.warn(`[WorkflowUtils] Basic auth endpoint ${endpoint} failed:`, endpointError.message);
          // Continue to the next endpoint
        }
      }
    }
    
    if (!response) {
      // If no API endpoints work, try to extract services from the workflow definition
      console.log('[WorkflowUtils] All API endpoints failed, attempting to extract services from workflow definition...');
      
      try {
        // Fetch the workflow first
        const workflow = await fetchWorkflowById(workflowId);
        
        if (workflow && workflow.definition) {
          let definition = workflow.definition;
          
          // If definition is a string, parse it
          if (typeof definition === 'string') {
            try {
              definition = JSON.parse(definition);
            } catch (error) {
              console.warn('[WorkflowUtils] Failed to parse workflow definition:', error);
              return [];
            }
          }
          
          // Extract services from the definition
          if (definition.steps && Array.isArray(definition.steps)) {
            console.log('[WorkflowUtils] Successfully extracted services from workflow definition');
            
            return definition.steps.map(step => ({
              id: step.serviceId || step.id,
              name: step.serviceName || step.name,
              serviceId: step.serviceId || step.id,
              serviceName: step.serviceName || step.name,
              required: step.required !== undefined ? step.required : true,
              governmentCharge: step.governmentCharge || '',
              serviceCharge: step.serviceCharge || '',
              taxRate: step.taxRate || ''
            }));
          }
          
          // If steps are directly on the workflow
          if (workflow.steps && Array.isArray(workflow.steps)) {
            console.log('[WorkflowUtils] Successfully extracted services from workflow steps');
            
            return workflow.steps.map(step => ({
              id: step.serviceId || step.id,
              name: step.serviceName || step.name,
              serviceId: step.serviceId || step.id,
              serviceName: step.serviceName || step.name,
              required: step.required !== undefined ? step.required : true,
              governmentCharge: step.governmentCharge || '',
              serviceCharge: step.serviceCharge || '',
              taxRate: step.taxRate || ''
            }));
          }
        }
      } catch (error) {
        console.warn('[WorkflowUtils] Error while trying to extract services from workflow definition:', error);
      }
      
      console.warn('[WorkflowUtils] Could not extract services from workflow definition, returning empty array');
      return [];
    }
    
    if (response && response.data) {
      const services = Array.isArray(response.data) ? response.data : [response.data];
      console.log('[WorkflowUtils] Successfully fetched workflow services:', services.length);
      
      // Standardize the service format
      return services.map(service => ({
        id: service.id || service.serviceId || service.serviceid || service.service_id,
        name: service.name || service.serviceName || service.servicename || service.service_name,
        serviceId: service.serviceId || service.id || service.serviceid || service.service_id,
        serviceName: service.serviceName || service.name || service.servicename || service.service_name,
        required: service.required !== undefined ? service.required : true,
        governmentCharge: service.governmentCharge || service.governmentcharge || service.government_charge || '',
        serviceCharge: service.serviceCharge || service.servicecharge || service.service_charge || '',
        taxRate: service.taxRate || service.taxrate || service.tax_rate || ''
      }));
    }
    
    // If we get here with no data, return an empty array
    console.warn('[WorkflowUtils] No services found for workflow:', workflowId);
    return [];
  } catch (error) {
    console.error('[WorkflowUtils] Error fetching workflow services:', error);
    throw error;
  }
};

/**
 * Fetch service mappings for a customer organization using token-based authentication
 * @param {string} customerId - The ID of the customer organization
 * @returns {Promise<Array>} The list of service mappings
 */
/**
 * Get authentication token for API calls
 * @returns {Promise<string|null>} Valid token or null if authentication failed
 */
export const getAuthToken = async () => {
  try {
    console.log('[WorkflowUtils] Getting auth token for API calls');
    
    // Try to get token from localStorage first
    let token = localStorage.getItem('token');
    if (token) {
      console.log('[WorkflowUtils] Token from localStorage exists');
      return `Bearer ${token}`;
    }
    
    // Try to get JWT token from localStorage
    token = localStorage.getItem('jwt_token');
    if (token) {
      console.log('[WorkflowUtils] JWT token from localStorage exists');
      return `Bearer ${token}`;
    }
    
    // Try to get token from authInfo in localStorage
    const authInfo = localStorage.getItem('authInfo');
    if (authInfo) {
      try {
        const authInfoObj = JSON.parse(authInfo);
        if (authInfoObj && authInfoObj.token) {
          console.log('[WorkflowUtils] Token from authInfo exists');
          return `Bearer ${authInfoObj.token}`;
        }
      } catch (parseError) {
        console.warn('[WorkflowUtils] Error parsing authInfo:', parseError);
      }
    }
    
    // If no token is found, throw an error
    throw new Error('No valid authentication token found');
  } catch (error) {
    console.error('[WorkflowUtils] Error getting auth token:', error);
    throw error; // Re-throw the error to be handled by the caller
  }
};

export const fetchServiceMappings = async (customerId) => {
  try {
    if (!customerId) {
      console.error('[WorkflowUtils] No customer ID provided');
      return []; // Return empty array instead of throwing
    }
    
    // Try to get token from localStorage first
    console.log('[WorkflowUtils] Getting token for service mappings...');
    let token = localStorage.getItem('token');
    console.log('[WorkflowUtils] Token exists:', !!token);
    
    // If no token in localStorage, try to get it from auth service
    if (!token) {
      try {
        token = await ensureValidToken();
        console.log('[WorkflowUtils] Token from auth service exists:', !!token);
      } catch (tokenError) {
        console.warn('[WorkflowUtils] Error getting token from auth service:', tokenError);
      }
    }
    
    // Try different API endpoint patterns - prioritize authenticated endpoints
    const possibleEndpoints = [
      // Standard authenticated API endpoint (PREFERRED)
      `${API_BASE_URL}/api/serviceMapping/mappedServices/${customerId}`,
      // Alternative authenticated endpoint structure
      `${API_BASE_URL}/api/service-mapping/mapped-services/${customerId}`,
      // Additional authenticated endpoints
      `${API_BASE_URL}/api/serviceMappings/${customerId}`,
      // Legacy endpoints as fallback
      `${API_BASE_URL}/v1/serviceMapping/mappedServices/${customerId}`,
      `${API_BASE_URL}/v1/serviceMappings/${customerId}`
    ];
    
    let response = null;
    let successEndpoint = '';
    
    // Try each endpoint with token authentication first
    if (token) {
      console.log('[WorkflowUtils] Trying endpoints with JWT token');
      
      for (const endpoint of possibleEndpoints) {
        try {
          console.log(`[WorkflowUtils] Trying endpoint with JWT token: ${endpoint}`);
          
          const result = await axios.get(endpoint, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (result && result.data) {
            response = result;
            successEndpoint = endpoint;
            console.log(`[WorkflowUtils] Successfully fetched service mappings with JWT token from ${successEndpoint}`);
            break;
          }
        } catch (endpointError) {
          console.warn(`[WorkflowUtils] JWT token endpoint ${endpoint} failed:`, endpointError.message);
          // Continue to the next endpoint
        }
      }
    }
    
    // If JWT token authentication failed, try with basic auth
    if (!response) {
      console.log('[WorkflowUtils] JWT token authentication failed, trying with basic auth');
      
      for (const endpoint of possibleEndpoints) {
        try {
          console.log(`[WorkflowUtils] Trying endpoint with basic auth: ${endpoint}`);
          
          const result = await axios.get(endpoint, {
            headers: {
              'Authorization': `Basic ${btoa('admin:admin')}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (result && result.data) {
            response = result;
            successEndpoint = endpoint;
            console.log(`[WorkflowUtils] Successfully fetched service mappings with basic auth from ${successEndpoint}`);
            break;
          }
        } catch (endpointError) {
          console.warn(`[WorkflowUtils] Basic auth endpoint ${endpoint} failed:`, endpointError.message);
          // Continue to the next endpoint
        }
      }
    }
    
    if (!response) {
      console.warn('[WorkflowUtils] All API endpoints failed for service mappings, returning empty array');
      return []; // Return empty array instead of throwing to allow fallbacks
    }
    
    if (response && response.data) {
      const mappings = Array.isArray(response.data) ? response.data : [response.data];
      console.log('[WorkflowUtils] Successfully fetched service mappings:', mappings.length);
      
      // Standardize the mapping format and ensure all possible ID fields are captured
      return mappings.map(mapping => {
        // Log the raw mapping data for debugging
        console.log('[WorkflowUtils] Raw mapping data:', mapping);
        
        // Extract all possible ID fields
        const serviceId = mapping.id || mapping.serviceId || mapping.serviceid || mapping.service_id || '';
        const serviceName = mapping.name || mapping.serviceName || mapping.servicename || mapping.service_name || '';
        
        // Extract all possible pricing fields with different case variations
        const governmentCharge = 
          mapping.governmentCharge || 
          mapping.governmentcharge || 
          mapping.government_charge || 
          mapping.GovernmentCharge || 
          '';
        
        const serviceCharge = 
          mapping.serviceCharge || 
          mapping.servicecharge || 
          mapping.service_charge || 
          mapping.ServiceCharge || 
          '';
        
        const taxRate = 
          mapping.taxRate || 
          mapping.taxrate || 
          mapping.tax_rate || 
          mapping.TaxRate || 
          '';
        
        // Extract the actual fields from the API response format
        // The API returns: id, name, serviceCharge, taxOnServiceCharge, govtCharge
        const govtCharge = mapping.govtCharge || mapping.governmentCharge || mapping.government_charge || '';
        const actualServiceCharge = mapping.serviceCharge || mapping.service_charge || '';
        const taxOnServiceCharge = mapping.taxOnServiceCharge || mapping.taxRate || mapping.tax_rate || '';
        
        console.log('[WorkflowUtils] Extracted pricing from API response:', {
          govtCharge,
          serviceCharge: actualServiceCharge,
          taxOnServiceCharge
        });
        
        // Create a standardized mapping object with all possible ID variations
        return {
          id: serviceId,
          serviceId: serviceId, // Add explicit serviceId field
          name: serviceName,
          serviceName: serviceName,
          // Map the API fields to our expected fields
          governmentCharge: govtCharge, // Using govtCharge from API
          serviceCharge: actualServiceCharge, // Using serviceCharge from API
          taxRate: taxOnServiceCharge, // Using taxOnServiceCharge from API
          // Include original fields for reference
          govtCharge: govtCharge,
          taxOnServiceCharge: taxOnServiceCharge,
          isMapped: true, // Explicitly mark as mapped since it came from the API
          isServerMapped: true // Flag to indicate this came from the server
        };
      });
    }
    
    // If we get here with no data, return an empty array
    console.warn('[WorkflowUtils] No service mappings found for customer:', customerId);
    return [];
  } catch (error) {
    console.error('[WorkflowUtils] Error fetching service mappings:', error);
    throw error;
  }
};
